/**
 * Logging Context for developer debugging
 * Provides in-app logging that can be viewed in the LogViewer component
 */

import React, { createContext, useState, useCallback, useContext, type ReactNode } from 'react';
import type { LogEntry } from '../types';

interface LoggingContextValue {
  logs: LogEntry[];
  log: (level: 'info' | 'warn' | 'error' | 'debug', message: string, data?: unknown) => void;
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
  error: (message: string, data?: unknown) => void;
  debug: (message: string, data?: unknown) => void;
  clearLogs: () => void;
  isExpanded: boolean;
  toggleExpanded: () => void;
}

const LoggingContext = createContext<LoggingContextValue | undefined>(undefined);

export const useLogging = (): LoggingContextValue => {
  const context = useContext(LoggingContext);
  if (!context) {
    throw new Error('useLogging must be used within a LoggingProvider');
  }
  return context;
};

interface LoggingProviderProps {
  children: ReactNode;
}

export const LoggingProvider: React.FC<LoggingProviderProps> = ({ children }) => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);

  // Add a new log entry
  const log = useCallback((level: 'info' | 'warn' | 'error' | 'debug', message: string, data?: unknown) => {
    const timestamp = new Date().toISOString();
    const newLog: LogEntry = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      timestamp,
      level,
      message,
      data: data ? JSON.stringify(data, null, 2) : undefined
    };

    // Also log to the console
    if (level === 'error') {
      console.error(`[${timestamp}] ${message}`, data || '');
    } else if (level === 'warn') {
      console.warn(`[${timestamp}] ${message}`, data || '');
    } else {
      console.log(`[${timestamp}] ${message}`, data || '');
    }

    setLogs(prevLogs => {
      // Keep the 100 most recent logs
      const updatedLogs = [newLog, ...prevLogs];
      if (updatedLogs.length > 100) {
        return updatedLogs.slice(0, 100);
      }
      return updatedLogs;
    });
  }, []);

  // Clear all logs
  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  // Log level-specific methods
  const info = useCallback((message: string, data?: unknown) => log('info', message, data), [log]);
  const warn = useCallback((message: string, data?: unknown) => log('warn', message, data), [log]);
  const error = useCallback((message: string, data?: unknown) => log('error', message, data), [log]);
  const debug = useCallback((message: string, data?: unknown) => log('debug', message, data), [log]);

  // Toggle log viewer expansion
  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const contextValue: LoggingContextValue = {
    logs,
    log,
    info,
    warn,
    error,
    debug,
    clearLogs,
    isExpanded,
    toggleExpanded
  };

  return (
    <LoggingContext.Provider value={contextValue}>
      {children}
    </LoggingContext.Provider>
  );
};
